/* Warehouse 7.0.1: support the native mini-cart's full-height drawer layout. */
(() => {
  if (window.forestwestNativeDrawerInstalled) return;
  window.forestwestNativeDrawerInstalled = true;
  let cart = null;
  let host = null;
  let observer = null;
  let wasOpen = false;
  let headerResizeObserver = null;
  let drawerPositionFrame = null;
  let toolsRoot = null;
  let toolSheetPanel = null;
  let noteValue = null;
  let savedNote = null;
  let noteTimer = null;
  let notePromise = null;
  let noteError = false;
  let carouselRoot = null;
  let carouselResizeObserver = null;
  let carouselIndex = 0;
  let carouselKey = null;
  let carouselTargetIndex = null;
  let recommendationsRoot = null;
  let recommendationsController = null;
  const recommendationCache = new Map();
  const panels = { shipping: false, note: false };
  const shipping = { country: 'Australia', province: '', zip: '', requestId: 0, controller: null, message: '' };

  function cartPath() {
    return (window.routes?.cartUrl || (window.Shopify?.routes?.root || '/') + 'cart').replace(/\/$/, '');
  }

  function updateCarousel(index) {
    if (!carouselRoot) return;
    const cards = Array.from(carouselRoot.querySelectorAll('.fw-cart-recommendation'));
    carouselIndex = Math.max(0, Math.min(index, cards.length - 1));
    carouselKey = cards[carouselIndex]?.dataset.fwRecommendKey || null;
    cards.forEach((card, i) => {
      card.inert = i !== carouselIndex;
      card.setAttribute('aria-hidden', i === carouselIndex ? 'false' : 'true');
      card.setAttribute('aria-label', `${i + 1} of ${cards.length}`);
    });
    carouselRoot.querySelector('[data-fw-recommend-prev]').disabled = carouselIndex === 0;
    carouselRoot.querySelector('[data-fw-recommend-next]').disabled = carouselIndex >= cards.length - 1;
    carouselRoot.querySelector('[data-fw-carousel-nav]').hidden = cards.length <= 1;
    carouselRoot.querySelector('[data-fw-carousel-status]').textContent = `Recommendation ${carouselIndex + 1} of ${cards.length}`;
  }

  function showRecommendation(index, animate = true) {
    if (!carouselRoot) return;
    updateCarousel(index);
    const card = carouselRoot.querySelectorAll('.fw-cart-recommendation')[carouselIndex];
    carouselTargetIndex = animate ? carouselIndex : null;
    carouselRoot.querySelector('.fw-recommendation-list').scrollTo({
      left: card?.offsetLeft || 0,
      behavior: animate && !window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'auto'
    });
  }

  function hydrateCarousel() {
    const section = cart?.querySelector('.fw-cart-recommendations');
    const next = section?.querySelector('.fw-cart-recommendation') ? section : null;
    if (next === carouselRoot) return;
    carouselResizeObserver?.disconnect();
    carouselRoot = next;
    if (!carouselRoot) return;
    const cards = Array.from(next.querySelectorAll('.fw-cart-recommendation'));
    const existing = cards.findIndex(card => card.dataset.fwRecommendKey === carouselKey);
    carouselIndex = existing >= 0 ? existing : Math.min(carouselIndex, cards.length - 1);
    const track = next.querySelector('.fw-recommendation-list');
    showRecommendation(carouselIndex, false);
    let scrollFrame = null;
    track.addEventListener('scroll', () => {
      if (scrollFrame !== null) return;
      scrollFrame = requestAnimationFrame(() => {
        scrollFrame = null;
        if (next !== carouselRoot) return;
        if (carouselTargetIndex !== null) {
          if (Math.abs(cards[carouselTargetIndex].offsetLeft - track.scrollLeft) > 1) return;
          carouselTargetIndex = null;
        }
        let nearest = 0;
        cards.forEach((card, i) => {
          if (Math.abs(card.offsetLeft - track.scrollLeft) < Math.abs(cards[nearest].offsetLeft - track.scrollLeft)) nearest = i;
        });
        if (nearest !== carouselIndex) updateCarousel(nearest);
      });
    }, { passive: true });
    track.addEventListener('scrollend', () => {
      if (next !== carouselRoot) return;
      carouselTargetIndex = null;
      const nearest = cards.reduce((best, card, i) => Math.abs(card.offsetLeft - track.scrollLeft) < Math.abs(cards[best].offsetLeft - track.scrollLeft) ? i : best, 0);
      updateCarousel(nearest);
    });
    carouselResizeObserver = new ResizeObserver(() => {
      if (next === carouselRoot) showRecommendation(carouselIndex, false);
    });
    carouselResizeObserver.observe(track);
  }

  function recommendationMoney(cents) {
    const currency = window.Shopify?.currency?.active || 'AUD';
    return new Intl.NumberFormat(window.Shopify?.locale || 'en-AU', { style: 'currency', currency }).format(Number(cents) / 100);
  }

  function makeRecommendationCard(product) {
    const available = product.variants.filter(variant => variant.available && Number(variant.price) > 0);
    const variant = available[0];
    const card = document.createElement('article');
    card.className = 'fw-cart-recommendation';
    card.dataset.fwRecommendKey = product.id;
    const imageLink = document.createElement('a');
    imageLink.className = 'fw-recommendation-image';
    imageLink.href = product.url;
    imageLink.tabIndex = -1;
    imageLink.setAttribute('aria-hidden', 'true');
    const imageUrl = typeof product.featured_image === 'string' ? product.featured_image : product.featured_image?.src;
    if (imageUrl) {
      const image = document.createElement('img');
      image.src = imageUrl;
      image.alt = product.title;
      image.loading = 'lazy';
      imageLink.append(image);
    }
    const info = document.createElement('div');
    info.className = 'fw-recommendation-info';
    const title = document.createElement('a');
    title.className = 'fw-recommendation-title';
    title.href = product.url;
    title.textContent = product.title;
    info.append(title);
    if (product.variants.length > 1 || variant.title !== 'Default Title') {
      const select = document.createElement('select');
      select.className = 'fw-recommendation-variant';
      select.setAttribute('data-fw-recommend-variant', '');
      select.setAttribute('aria-label', `Choose options for ${product.title}`);
      available.forEach(option => {
        const element = new Option(option.title, option.id);
        element.dataset.price = recommendationMoney(option.price);
        element.dataset.min = option.quantity_rule?.min || 1;
        select.add(element);
      });
      info.append(select);
    }
    const actions = document.createElement('div');
    actions.className = 'fw-recommendation-actions';
    const price = document.createElement('strong');
    price.setAttribute('data-fw-recommend-price', '');
    price.textContent = recommendationMoney(variant.price);
    const add = document.createElement('button');
    add.type = 'button';
    add.setAttribute('data-fw-recommend-add', '');
    add.dataset.variantId = variant.id;
    add.dataset.quantity = variant.quantity_rule?.min || 1;
    add.setAttribute('aria-label', `Add ${product.title} to cart`);
    add.textContent = 'Add +';
    actions.append(price, add);
    info.append(actions);
    card.append(imageLink, info);
    return card;
  }

  async function hydrateRecommendations() {
    const next = cart?.querySelector('.fw-cart-recommendations') || null;
    if (next === recommendationsRoot) return;
    recommendationsController?.abort();
    recommendationsRoot = next;
    if (!next || next.querySelector('.fw-cart-recommendation')) return;
    const ids = (next.dataset.fwRecommendProductIds || '').split(',').filter(id => /^\d+$/.test(id));
    if (!ids.length) return;
    recommendationsController = new AbortController();
    const signal = recommendationsController.signal;
    const inCart = new Set(ids);
    const seen = new Set();
    const products = [];
    const endpoint = (window.routes?.productRecommendationsUrl || (window.Shopify?.routes?.root || '/') + 'recommendations/products') + '.json';
    try {
      for (const intent of ['complementary', 'related']) {
        for (const id of ids.slice(0, 3)) {
          const key = `${ids.join(',')}:${id}:${intent}`;
          let data = recommendationCache.get(key);
          if (!data) {
            const query = new URLSearchParams({ product_id: id, intent, limit: '10' });
            const response = await fetch(`${endpoint}?${query}`, { credentials: 'same-origin', signal });
            if (!response.ok) continue;
            data = await response.json();
            recommendationCache.set(key, data);
          }
          for (const product of data.products || []) {
            if (inCart.has(String(product.id)) || seen.has(String(product.id)) || !product.available || product.gift_card || !product.variants?.some(variant => variant.available && Number(variant.price) > 0)) continue;
            seen.add(String(product.id));
            products.push(product);
            if (products.length === 6) break;
          }
          if (products.length === 6) break;
        }
        if (products.length) break;
      }
      if (signal.aborted || next !== recommendationsRoot || !next.isConnected || !products.length) return;
      const list = next.querySelector('.fw-recommendation-list');
      products.forEach(product => list.append(makeRecommendationCard(product)));
      next.hidden = false;
      hydrateCarousel();
    } catch (error) {
      if (!signal.aborted) next.hidden = true;
    }
  }

  function noteStatus(message, error = false) {
    const status = cart?.querySelector('[data-fw-note-status]');
    const summary = cart?.querySelector('[data-fw-note-summary]');
    if (status) { status.textContent = message; status.style.color = error ? '#b83c36' : ''; }
    if (summary) summary.textContent = error ? 'Not saved' : notePromise ? 'Saving…' : noteValue !== savedNote ? 'Not saved' : noteValue ? 'Saved' : '';
  }

  function invalidateShipping(message = '') {
    shipping.requestId++;
    shipping.controller?.abort();
    shipping.controller = null;
    shipping.message = message;
    const results = cart?.querySelector('[data-fw-shipping-results]');
    if (results) { results.textContent = message; results.classList.remove('fw-shipping-error'); }
    const button = cart?.querySelector('[data-fw-estimate]');
    if (button) { button.disabled = false; button.textContent = 'Estimate'; }
  }

  function populateProvinces() {
    const country = cart?.querySelector('[data-fw-field="country"]');
    const province = cart?.querySelector('[data-fw-field="province"]');
    const wrapper = cart?.querySelector('[data-fw-province-wrapper]');
    if (!country || !province || !wrapper) return;
    let regions = [];
    try { regions = JSON.parse(country.selectedOptions[0]?.dataset.provinces || '[]'); } catch {}
    province.replaceChildren(new Option('Select state', ''));
    regions.forEach(region => {
      const value = Array.isArray(region) ? region[0] : region;
      const label = Array.isArray(region) ? region[1] || region[0] : region;
      province.add(new Option(label, value));
    });
    wrapper.hidden = regions.length === 0;
    province.value = shipping.province;
    shipping.province = province.value;
  }

  function hydrateTools() {
    const next = cart?.querySelector('.fw-cart-tools') || null;
    if (next === toolsRoot) return;
    if (toolsRoot) invalidateShipping('Cart updated. Please estimate shipping again.');
    toolsRoot = next;
    if (!toolsRoot) return;
    const country = cart.querySelector('[data-fw-field="country"]');
    country.value = shipping.country;
    if (!country.value) country.selectedIndex = 0;
    shipping.country = country.value;
    populateProvinces();
    cart.querySelector('[data-fw-field="zip"]').value = shipping.zip;
    cart.querySelector('[data-fw-shipping-results]').textContent = shipping.message;
    cart.querySelectorAll('[data-fw-panel]').forEach(panel => { panel.open = panels[panel.dataset.fwPanel]; });
    const field = cart.querySelector('[data-fw-note]');
    if (noteValue === null) noteValue = savedNote = field.value;
    else field.value = noteValue;
    noteStatus(noteError ? 'Could not save your note. Please try again.' : noteValue === savedNote ? (noteValue ? 'Note saved.' : 'Optional') : 'Not saved yet.', noteError);
  }

  async function jsonResponse(response) {
    const text = await response.text();
    let data = null;
    if (text) { try { data = JSON.parse(text); } catch { throw new Error('Unable to read the response. Please try again.'); } }
    if (!response.ok) throw new Error('Please check the address and try again.');
    return data;
  }

  async function addRecommendation(button) {
    const card = button.closest('.fw-cart-recommendation');
    const select = card.querySelector('[data-fw-recommend-variant]');
    const selected = select?.selectedOptions[0];
    const id = selected?.value || button.dataset.variantId;
    const quantity = Number(selected?.dataset.min || button.dataset.quantity || 1);
    const status = cart.querySelector('[data-fw-recommend-status]');
    button.disabled = true;
    button.textContent = 'Adding…';
    if (status) status.textContent = '';
    try {
      const response = await fetch(`${cartPath()}/add.js`, {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id, quantity }] })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(typeof data.description === 'string' ? data.description : 'Could not add this item. Please try again.');
      document.documentElement.dispatchEvent(new CustomEvent('product:added', { bubbles: true, detail: { quantity, productAdded: true } }));
      button.textContent = 'Added';
    } catch (error) {
      if (status) status.textContent = error.message || 'Could not add this item. Please try again.';
      button.textContent = 'Add +';
      button.disabled = false;
    }
  }

  async function estimateShipping() {
    const button = cart?.querySelector('[data-fw-estimate]');
    const results = cart?.querySelector('[data-fw-shipping-results]');
    if (!button || !results) return;
    shipping.country = cart.querySelector('[data-fw-field="country"]').value;
    shipping.province = cart.querySelector('[data-fw-field="province"]').value;
    shipping.zip = cart.querySelector('[data-fw-field="zip"]').value.trim();
    invalidateShipping();
    const id = shipping.requestId;
    if (!shipping.zip || (shipping.country === 'Australia' && !/^\d{4}$/.test(shipping.zip))) {
      results.textContent = shipping.country === 'Australia' ? 'Enter a valid four-digit postcode.' : 'Enter your postcode.';
      cart.querySelector('[data-fw-field="zip"]').focus();
      return;
    }
    if (!cart.querySelector('[data-fw-province-wrapper]').hidden && !shipping.province) {
      results.textContent = 'Select your state or territory.';
      cart.querySelector('[data-fw-field="province"]').focus();
      return;
    }
    shipping.controller = new AbortController();
    const signal = shipping.controller.signal;
    const query = new URLSearchParams({ 'shipping_address[country]': shipping.country, 'shipping_address[zip]': shipping.zip });
    if (shipping.province) query.set('shipping_address[province]', shipping.province);
    button.disabled = true;
    button.textContent = 'Calculating…';
    results.textContent = 'Calculating shipping options…';
    try {
      await jsonResponse(await fetch(`${cartPath()}/prepare_shipping_rates.json?${query}`, { method: 'POST', credentials: 'same-origin', signal }));
      let data = null;
      for (let attempt = 0; attempt < 40; attempt++) {
        if (signal.aborted) return;
        data = await jsonResponse(await fetch(`${cartPath()}/async_shipping_rates.json?${query}`, { credentials: 'same-origin', signal }));
        if (data !== null) break;
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      if (id !== shipping.requestId || !results.isConnected) return;
      if (!data || !Array.isArray(data.shipping_rates)) throw new Error('Shipping is taking longer than expected. Please try again.');
      results.replaceChildren();
      if (data.shipping_rates.length === 0) { results.textContent = 'No shipping options were found for this address. Please contact us.'; return; }
      const label = document.createElement('p');
      label.className = 'fw-shipping-count';
      label.textContent = data.shipping_rates.length === 1
        ? (window.languages?.shippingEstimatorOneResult || 'There is one shipping rate for your address:')
        : (window.languages?.shippingEstimatorMultipleResults || 'There are {{count}} shipping rates for your address:').replace('{{count}}', data.shipping_rates.length);
      const list = document.createElement('ul');
      data.shipping_rates.forEach(rate => {
        const row = document.createElement('li');
        const rateLine = document.createElement('div');
        rateLine.className = 'fw-shipping-rate-line';
        const name = document.createElement('span');
        // Warehouse's product-page estimator displays rate.name unchanged.
        name.textContent = rate.name;
        const price = document.createElement('strong');
        const currency = rate.currency || window.Shopify?.currency?.active || 'AUD';
        try { price.textContent = new Intl.NumberFormat(window.Shopify?.locale || document.documentElement.lang || 'en-AU', { style: 'currency', currency }).format(Number(rate.price)); }
        catch { price.textContent = Number(rate.price).toFixed(2) + ' ' + currency; }
        rateLine.append(name, price);
        row.append(rateLine);
        list.append(row);
      });
      results.append(label, list);
    } catch (error) {
      if (signal.aborted || id !== shipping.requestId) return;
      results.textContent = error.message || 'We could not estimate shipping. Please try again.';
      results.classList.add('fw-shipping-error');
    } finally {
      if (id === shipping.requestId && button.isConnected) { button.disabled = false; button.textContent = 'Estimate'; }
    }
  }

  function persistNote() {
    clearTimeout(noteTimer);
    if (notePromise) return notePromise;
    if (noteValue === null || noteValue === savedNote) { noteStatus(noteValue ? 'Note saved.' : 'Optional'); return Promise.resolve(); }
    noteError = false;
    noteStatus('Saving note…');
    notePromise = (async () => {
      while (noteValue !== savedNote) {
        const value = noteValue;
        await jsonResponse(await fetch(`${cartPath()}/update.js`, { method: 'POST', credentials: 'same-origin', keepalive: true, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note: value }) }));
        savedNote = value;
      }
    })().catch(error => { noteError = true; throw error; }).finally(() => {
      notePromise = null;
      noteStatus(noteError ? 'Could not save your note. Please try again.' : 'Note saved.', noteError);
    });
    noteStatus('Saving note…');
    return notePromise;
  }

  function isOpen() {
    return cart?.isConnected && cart.getAttribute('aria-hidden') === 'false';
  }

  function mobileToolPanel() {
    if (!isOpen() || !window.matchMedia('(max-width: 480px)').matches) return null;
    return cart.querySelector('[data-fw-panel][open]');
  }

  function syncToolSheet() {
    const panel = mobileToolPanel();
    const backdrop = cart?.querySelector('.fw-cart-tool-backdrop');
    if (backdrop) backdrop.hidden = !panel;
    if (panel === toolSheetPanel) return;
    toolSheetPanel = panel;
    if (panel) requestAnimationFrame(() => {
      if (mobileToolPanel() === panel) panel.querySelector('[data-fw-close-tool]')?.focus({ preventScroll: true });
    });
  }

  function closeToolSheet() {
    const panel = mobileToolPanel();
    if (!panel) return;
    panel.open = false;
    panels[panel.dataset.fwPanel] = false;
    syncToolSheet();
    panel.querySelector('summary')?.focus({ preventScroll: true });
  }

  function focusCloseButton() {
    const close = mobileToolPanel()?.querySelector('[data-fw-close-tool]') || cart?.querySelector('.fw-cart-close');
    close?.focus({ preventScroll: true });
  }

  // Leave the visible site header above the drawer and its backdrop.
  function updateDrawerPosition() {
    drawerPositionFrame = null;
    if (!cart) return;
    const header = document.querySelector('header.header');
    const top = Math.max(0, Math.ceil(header?.getBoundingClientRect().bottom || 0));
    cart.style.setProperty('--fw-cart-top', `${top}px`);
    syncToolSheet();
  }

  function scheduleDrawerPosition() {
    if (!isOpen() || drawerPositionFrame !== null) return;
    drawerPositionFrame = requestAnimationFrame(updateDrawerPosition);
  }

  function sync() {
    hydrateTools();
    hydrateRecommendations();
    hydrateCarousel();
    const open = Boolean(isOpen());
    document.documentElement.classList.toggle('fw-cart-drawer-open', open);
    host?.classList.toggle('fw-cart-drawer-host--open', open);
    updateDrawerPosition();
    if (open && (!wasOpen || !cart.contains(document.activeElement))) {
      requestAnimationFrame(() => { if (isOpen()) focusCloseButton(); });
    }
    if (wasOpen && !open) {
      if (typeof Accessibility !== 'undefined' && Accessibility.listeners?.['mini-cart']) {
        Accessibility.removeTrapFocus(cart, 'mini-cart');
      }
      document.querySelector('.header__cart-toggle')?.focus({ preventScroll: true });
    }
    wasOpen = open;
  }

  function bind() {
    const next = document.querySelector('#mini-cart.fw-cart-drawer');
    if (next === cart) return;
    observer?.disconnect();
    headerResizeObserver?.disconnect();
    host?.classList.remove('fw-cart-drawer-host--open');
    cart = next;
    host = cart?.closest('.shopify-section') || cart?.closest('[data-section-type="header"]');
    wasOpen = false;
    if (cart) {
      observer = new MutationObserver(sync);
      observer.observe(cart, { childList: true, attributes: true, attributeFilter: ['aria-hidden'] });
      const header = document.querySelector('header.header');
      if (header) {
        headerResizeObserver = new ResizeObserver(scheduleDrawerPosition);
        headerResizeObserver.observe(header);
      }
    }
    sync();
  }

  document.addEventListener('toggle', event => {
    const panel = event.target;
    if (!cart?.contains(panel) || !panel.matches('[data-fw-panel]')) return;
    panels[panel.dataset.fwPanel] = panel.open;
    if (panel.open) cart.querySelectorAll('[data-fw-panel]').forEach(other => {
      if (other !== panel) { other.open = false; panels[other.dataset.fwPanel] = false; }
    });
    syncToolSheet();
  }, true);

  document.addEventListener('input', event => {
    if (!cart?.contains(event.target)) return;
    if (event.target.matches('[data-fw-note]')) {
      noteValue = event.target.value;
      noteError = false;
      noteStatus('Not saved yet.');
      clearTimeout(noteTimer);
      noteTimer = setTimeout(() => { persistNote().catch(() => {}); }, 650);
    } else if (event.target.matches('[data-fw-field="zip"]')) {
      shipping.zip = event.target.value;
      invalidateShipping();
    }
  });
  document.addEventListener('change', event => {
    if (!cart?.contains(event.target)) return;
    if (event.target.matches('[data-fw-field="country"]')) {
      shipping.country = event.target.value;
      shipping.province = '';
      populateProvinces();
      invalidateShipping();
    } else if (event.target.matches('[data-fw-field="province"]')) {
      shipping.province = event.target.value;
      invalidateShipping();
    } else if (event.target.matches('[data-fw-note]')) persistNote().catch(() => {});
    else if (event.target.matches('[data-fw-recommend-variant]')) {
      const card = event.target.closest('.fw-cart-recommendation');
      card.querySelector('[data-fw-recommend-price]').textContent = event.target.selectedOptions[0]?.dataset.price || '';
    }
  });
  document.addEventListener('click', event => {
    if (!cart?.contains(event.target)) return;
    if (event.target.closest('[data-fw-estimate]')) { event.preventDefault(); estimateShipping(); }
    else if (event.target.closest('[data-fw-save-note]')) { event.preventDefault(); persistNote().then(closeToolSheet).catch(() => {}); }
    else if (event.target.closest('[data-fw-close-tool]')) { event.preventDefault(); closeToolSheet(); }
    else if (event.target.closest('[data-fw-recommend-add]')) { event.preventDefault(); addRecommendation(event.target.closest('[data-fw-recommend-add]')); }
    else if (event.target.closest('[data-fw-recommend-prev]')) { event.preventDefault(); showRecommendation(carouselIndex - 1); }
    else if (event.target.closest('[data-fw-recommend-next]')) { event.preventDefault(); showRecommendation(carouselIndex + 1); }
    else {
      const viewCart = event.target.closest('.mini-cart__button-container a');
      if (viewCart && !event.defaultPrevented && (notePromise || (noteValue !== null && noteValue !== savedNote))) {
        event.preventDefault();
        persistNote().then(() => { window.location.assign(viewCart.href); }).catch(() => {});
      }
    }
  });
  document.addEventListener('submit', event => {
    if (event.target !== cart || (!notePromise && (noteValue === null || noteValue === savedNote))) return;
    event.preventDefault();
    const submitter = event.submitter || cart.querySelector('[name="checkout"]');
    persistNote().then(() => { if (cart?.isConnected) cart.requestSubmit(submitter); }).catch(() => {});
  });

  document.addEventListener('keydown', event => {
    if (!isOpen()) return;
    if (event.target.closest('.fw-cart-recommendations') && !event.target.matches('input, textarea, select') && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
      event.preventDefault();
      showRecommendation(carouselIndex + (event.key === 'ArrowRight' ? 1 : -1));
      return;
    }
    if (event.key === 'Enter' && event.target.matches('[data-fw-field="zip"]')) { event.preventDefault(); estimateShipping(); return; }
    if (event.key === 'Escape') {
      event.preventDefault();
      if (mobileToolPanel()) { closeToolSheet(); return; }
      document.querySelector('.header__cart-toggle[data-action="toggle-mini-cart"]')?.click();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusRoot = mobileToolPanel()?.querySelector('.fw-cart-tool-body') || cart;
    const focusable = Array.from(focusRoot.querySelectorAll('summary, a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]'))
      .filter(element => element.tabIndex >= 0 && element.getClientRects().length && !element.closest('[inert], [aria-hidden="true"]'));
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && (document.activeElement === first || !focusRoot.contains(document.activeElement))) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && (document.activeElement === last || !focusRoot.contains(document.activeElement))) {
      event.preventDefault();
      first?.focus();
    }
  });

  document.addEventListener('focusin', event => {
    if (isOpen() && !cart.contains(event.target)) focusCloseButton();
  });
  window.addEventListener('resize', scheduleDrawerPosition, { passive: true });
  window.addEventListener('scroll', scheduleDrawerPosition, { passive: true });
  window.visualViewport?.addEventListener('resize', scheduleDrawerPosition, { passive: true });
  document.addEventListener('cart:rerendered', sync, true);
  document.addEventListener('shopify:section:load', bind);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bind, { once: true });
  else bind();
})();
